import { ClientDomainDNSMonitoring, DNSMonitoringStatus } from '@numeo/types-core'
import axios from 'axios'

interface PaginationMeta {
    page: number
    limit: number
    total: number
    totalPages: number
}

interface DNSMonitoringResponse {
    success: boolean
    data: {
        items: Array<{
            projectName: string
            results: ClientDomainDNSMonitoring[]
        }>
        pagination: PaginationMeta
    }
}

interface ApplicationDNSStatusResponse {
    success: boolean
    data: {
        projectName: string
        domains: ClientDomainDNSMonitoring[]
    }
}

interface Filters {
    projectName?: string
    domain?: string
    status?: DNSMonitoringStatus
}

/**
 * DNS Monitoring API Client for Gluon (Super Admin View)
 * Provides paginated list of all applications and their DNS monitoring status
 */
class DNSMonitoringApi {
    private readonly DNS_URL = `/security`

    /**
     * Get DNS monitoring status for all applications with pagination
     * Super admin view - shows all applications
     */
    async getAllApplicationsDNS(page: number = 1, limit: number = 20, filters?: Filters): Promise<DNSMonitoringResponse> {
        const params = new URLSearchParams()
        params.append('page', page.toString())
        params.append('limit', limit.toString())

        if (filters?.projectName) params.append('projectName', filters.projectName)
        if (filters?.domain) params.append('domain', filters.domain)
        if (filters?.status) params.append('status', filters.status)

        const response = await axios.get<DNSMonitoringResponse>(`${this.DNS_URL}/all?${params.toString()}`)
        return response.data
    }

    /**
     * Get DNS monitoring status for a specific application
     */
    async getApplicationDNS(projectName: string): Promise<ClientDomainDNSMonitoring[]> {
        const response = await axios.get<ApplicationDNSStatusResponse>(`${this.DNS_URL}/status/${projectName}`)
        return response.data.data.domains || []
    }

    /**
     * Trigger DNS check for a specific application
     */
    async triggerDNSCheck(projectName: string): Promise<ClientDomainDNSMonitoring[]> {
        const response = await axios.post<{
            success: boolean
            data: { projectName: string; domainsChecked: number; results: ClientDomainDNSMonitoring[] }
        }>(`${this.DNS_URL}/check`, { projectName })

        return response.data.data.results || []
    }

    /**
     * Trigger DNS check for all applications
     */
    async triggerCheckAll(limit: number = 30): Promise<{ projectName: string; results: ClientDomainDNSMonitoring[] }[]> {
        const response = await axios.post<{
            success: boolean
            data: {
                applicationsChecked: number
                totalDomainsChecked: number
                suspiciousApplications: number
                results: Array<{ projectName: string; results: ClientDomainDNSMonitoring[] }>
            }
        }>(`${this.DNS_URL}/check-all`, { limit })

        return response.data.data.results || []
    }
}

// Export singleton instance
export const dnsMonitoringApi = new DNSMonitoringApi()
